import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { systemRolesAPI } from '../utils/api';
import { toast } from 'sonner';
import { Shield, Loader2, Plus, Trash2, Save, RefreshCw, Check, X, Users } from 'lucide-react';

export default function SystemRoles() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [catalog, setCatalog] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null); // {label, description, menu_permissions:Set}
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newRole, setNewRole] = useState({ name: '', label: '' });

  const selected = roles.find((r) => r.id === selectedId);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([systemRolesAPI.menuCatalog(), systemRolesAPI.list()]);
      setCatalog(c.data || []);
      setRoles(r.data || []);
      setSelectedId((prev) => prev || (r.data?.[0]?.id ?? null));
    } catch { toast.error('Failed to load roles'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (selected) {
      setDraft({
        label: selected.label || '',
        description: selected.description || '',
        perms: new Set(selected.menu_permissions || []),
      });
    } else { setDraft(null); }
  }, [selectedId, roles]);

  const groups = catalog.reduce((a, m) => { (a[m.group] = a[m.group] || []).push(m); return a; }, {});

  const togglePerm = (key) => {
    setDraft((d) => {
      const p = new Set(d.perms);
      if (p.has(key)) p.delete(key); else p.add(key);
      return { ...d, perms: p };
    });
  };
  const toggleGroup = (items, on) => {
    setDraft((d) => {
      const p = new Set(d.perms);
      items.forEach((m) => (on ? p.add(m.key) : p.delete(m.key)));
      return { ...d, perms: p };
    });
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await systemRolesAPI.update(selected.id, {
        label: draft.label, description: draft.description, menu_permissions: Array.from(draft.perms),
      });
      await load();
      toast.success('Role saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };

  const createRole = async () => {
    if (!newRole.name.trim()) { toast.error('Enter a role name'); return; }
    try {
      const res = await systemRolesAPI.create({ name: newRole.name.trim(), label: newRole.label.trim() || newRole.name.trim(), menu_permissions: ['dashboard'] });
      setCreating(false); setNewRole({ name: '', label: '' });
      await load();
      setSelectedId(res.data.id);
      toast.success('Role created');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create role'); }
  };

  const removeRole = async (r) => {
    if (!window.confirm(`Delete role "${r.label}"?`)) return;
    try {
      await systemRolesAPI.remove(r.id);
      setSelectedId(null);
      await load();
      toast.success('Role deleted');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'object' ? d.message : (d || 'Could not delete'));
    }
  };

  const runSeed = async () => {
    setSeeding(true);
    try { await systemRolesAPI.seed(); await load(); toast.success('Default roles loaded'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Seed failed'); }
    finally { setSeeding(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-teal-600" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <h2 className="font-semibold text-slate-800 flex items-center gap-2"><Shield className="w-4 h-4 text-teal-600" /> System Roles</h2>
        {isAdmin && (
          <div className="flex gap-2">
            <button onClick={runSeed} disabled={seeding} className="inline-flex items-center gap-1 border border-slate-300 text-slate-700 hover:bg-slate-100 px-3 py-1.5 rounded-lg text-sm">
              {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Load defaults
            </button>
            {!creating && (
              <button onClick={() => setCreating(true)} data-testid="role-add-btn" className="inline-flex items-center gap-1 bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium">
                <Plus className="w-4 h-4" /> Add role
              </button>
            )}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-[220px_1fr] gap-0">
        {/* Roles list */}
        <div className="border-r border-slate-100 p-3">
          {creating && (
            <div className="mb-2 p-2 rounded-lg bg-teal-50 border border-teal-200 space-y-2">
              <input autoFocus placeholder="Role name" value={newRole.name} onChange={(e) => setNewRole((n) => ({ ...n, name: e.target.value }))} data-testid="role-name-input" className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded-md" />
              <input placeholder="Display label" value={newRole.label} onChange={(e) => setNewRole((n) => ({ ...n, label: e.target.value }))} className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded-md" />
              <div className="flex gap-1">
                <button onClick={createRole} data-testid="role-create-save" className="flex-1 inline-flex items-center justify-center gap-1 bg-teal-600 text-white px-2 py-1.5 rounded-md text-sm"><Check className="w-4 h-4" /></button>
                <button onClick={() => { setCreating(false); setNewRole({ name: '', label: '' }); }} className="px-2 py-1.5 text-slate-400 hover:bg-slate-100 rounded-md"><X className="w-4 h-4" /></button>
              </div>
            </div>
          )}
          {roles.map((r) => (
            <button key={r.id} onClick={() => setSelectedId(r.id)} data-testid={`role-item-${r.name}`}
              className={`w-full text-left px-3 py-2 rounded-lg mb-1 ${selectedId === r.id ? 'bg-teal-600 text-white' : 'text-slate-700 hover:bg-slate-100'}`}>
              <div className="text-sm font-medium">{r.label}</div>
              <div className={`text-xs flex items-center gap-2 ${selectedId === r.id ? 'text-teal-100' : 'text-slate-400'}`}>
                <span>{r.menu_permissions.length} menus</span>
                <span className="inline-flex items-center gap-0.5"><Users className="w-3 h-3" />{r.assigned_user_count}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Editor */}
        <div className="p-4">
          {!selected || !draft ? (
            <div className="text-center py-16 text-slate-400">Select a role to edit its menu visibility.</div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="text-sm font-medium text-slate-600">Label</label>
                  <input disabled={!isAdmin} value={draft.label} onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg disabled:bg-slate-50" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Description</label>
                  <input disabled={!isAdmin} value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg disabled:bg-slate-50" />
                </div>
              </div>

              <p className="text-sm font-medium text-slate-700 mb-2">Visible menu items</p>
              <div className="space-y-4">
                {Object.entries(groups).map(([group, items]) => {
                  const allOn = items.every((m) => draft.perms.has(m.key));
                  return (
                    <div key={group} className="border border-slate-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold uppercase text-slate-500">{group}</span>
                        {isAdmin && (
                          <button onClick={() => toggleGroup(items, !allOn)} className="text-xs text-teal-700 hover:underline">
                            {allOn ? 'Clear all' : 'Select all'}
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {items.map((m) => (
                          <label key={m.key} className={`flex items-center gap-2 text-sm px-2 py-1.5 rounded-md cursor-pointer ${draft.perms.has(m.key) ? 'bg-teal-50 text-teal-800' : 'text-slate-600 hover:bg-slate-50'}`}>
                            <input type="checkbox" disabled={!isAdmin} checked={draft.perms.has(m.key)} onChange={() => togglePerm(m.key)} data-testid={`perm-${m.key}`} className="w-4 h-4 accent-teal-600" />
                            {m.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {isAdmin && (
                <div className="flex items-center justify-between mt-5">
                  <button onClick={() => removeRole(selected)} disabled={selected.name === 'admin'}
                    className="inline-flex items-center gap-1 text-red-600 hover:bg-red-50 px-3 py-2 rounded-lg text-sm disabled:opacity-40 disabled:cursor-not-allowed">
                    <Trash2 className="w-4 h-4" /> Delete role
                  </button>
                  <button onClick={save} disabled={saving} data-testid="role-save-btn"
                    className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
